import { useMemo, useState } from 'react'
import { RefreshCw } from 'lucide-react'
import { ApiError } from '@/shared/api/client.ts'
import { Card } from '@/shared/ui/card.tsx'
import { Chip } from '@/shared/ui/chip.tsx'
import { Alert, EmptyState, ErrorState, Skeleton } from '@/shared/ui/states.tsx'
import { formatTime, plural } from '@/shared/lib/format.ts'
import { ReleaseRow } from '@/features/updates/components/release-row.tsx'
import { UpcomingRow } from '@/features/updates/components/upcoming-row.tsx'
import { UpdatesSummary } from '@/features/updates/components/updates-summary.tsx'
import {
  areaFilters,
  filterReleases,
  filterUpcoming,
  kindFilters,
  matchesArea,
  matchesKind,
  type AreaFilter,
  type KindFilter,
} from '@/features/updates/filters.ts'
import { useRefreshUpdates, useUpdates } from '@/features/updates/use-updates.ts'

const COLLAPSED_COUNT = 4

export function UpdatesPage() {
  const { data, isPending, error, refetch } = useUpdates()
  const { mutate: refresh, isPending: isRefreshing } = useRefreshUpdates()

  const [kind, setKind] = useState<KindFilter>('Все')
  const [area, setArea] = useState<AreaFilter>('Все')
  const [expanded, setExpanded] = useState(false)
  const [openRows, setOpenRows] = useState<Record<string, boolean>>({})

  const releases = data?.releases ?? []
  const upcoming = data?.upcoming ?? []

  const visibleReleases = useMemo(() => filterReleases(releases, kind, area), [releases, kind, area])
  const visibleUpcoming = useMemo(() => filterUpcoming(upcoming, area), [upcoming, area])
  const shownReleases = expanded ? visibleReleases : visibleReleases.slice(0, COLLAPSED_COUNT)
  const hiddenCount = visibleReleases.length - shownReleases.length

  const toggleRow = (id: string) => setOpenRows((rows) => ({ ...rows, [id]: !rows[id] }))

  return (
    <>
      <header className="mb-6.5 flex flex-wrap items-end gap-x-5 gap-y-3.5">
        <div className="min-w-0 flex-1 basis-[320px]">
          <h1 className="mb-2 font-display text-[25px] leading-none font-semibold tracking-[-0.02em] md:text-[32px]">
            Обновления
          </h1>
          <p className="text-[15px] text-ink-muted text-balance-pretty">
            Что уже вышло и что готовим дальше. Список собирается из задач разработки сам, следить не нужно.
          </p>
        </div>

        <button
          type="button"
          onClick={() => refresh()}
          disabled={isRefreshing || isPending}
          className="inline-flex items-center gap-2 rounded-full border border-line bg-surface-soft px-3 py-1.5 text-[13px] whitespace-nowrap text-ink-muted transition-colors hover:text-ink disabled:opacity-60"
        >
          <RefreshCw className={isRefreshing ? 'size-3.5 animate-spin' : 'size-3.5'} />
          {isRefreshing ? 'Собираем…' : data?.meta.cachedAt ? `Обновлено в ${formatTime(data.meta.cachedAt)}` : 'Обновить'}
        </button>
      </header>

      {error && (
        <Card>
          <ErrorState
            message={error instanceof ApiError ? error.message : 'Не удалось получить обновления.'}
            onRetry={() => void refetch()}
          />
        </Card>
      )}

      {isPending && (
        <div className="flex flex-col gap-3.5">
          <Skeleton className="h-4 w-80" />
          <Skeleton className="h-9 w-full max-w-md rounded-full" />
          <Skeleton className="h-[320px] rounded-card" />
        </div>
      )}

      {data && (
        <>
          <UpdatesSummary meta={data.meta} upcomingCount={upcoming.length} />

          {data.meta.degraded && <Alert className="mb-5">{data.meta.degraded}</Alert>}

          <div className="mb-6 flex flex-wrap items-center gap-2">
            <span className="mr-0.5 text-[13px] text-ink-muted">Продукт</span>
            {areaFilters.map((filter) => (
              <Chip
                key={filter}
                label={filter}
                tone="tint"
                active={filter === area}
                count={
                  releases.filter((item) => matchesArea(item, filter)).length +
                  upcoming.filter((item) => matchesArea(item, filter)).length
                }
                onClick={() => {
                  setArea(filter)
                  setExpanded(false)
                }}
              />
            ))}
          </div>

          <section>
            <div className="mb-3.5 flex flex-wrap items-center justify-between gap-3">
              <h2 className="font-display text-[19px] font-semibold tracking-[-0.01em]">Недавние обновления</h2>
              <div className="flex flex-wrap gap-1.5">
                {kindFilters.map((filter) => (
                  <Chip
                    key={filter}
                    label={filter}
                    active={filter === kind}
                    count={releases.filter((item) => matchesKind(item, filter) && matchesArea(item, area)).length}
                    onClick={() => {
                      setKind(filter)
                      setExpanded(false)
                    }}
                  />
                ))}
              </div>
            </div>

            <Card className="overflow-hidden">
              {shownReleases.map((item) => (
                <ReleaseRow key={item.id} item={item} open={Boolean(openRows[item.id])} onToggle={() => toggleRow(item.id)} />
              ))}

              {visibleReleases.length === 0 && (
                <EmptyState
                  title="Здесь пока пусто"
                  description="В этой категории обновлений не было. Попробуйте снять фильтры."
                />
              )}

              {hiddenCount > 0 && (
                <button
                  type="button"
                  onClick={() => setExpanded(true)}
                  className="flex w-full items-center justify-center gap-2 border-t border-line-soft bg-surface-soft py-3.5 text-[13.5px] font-semibold text-brand transition-colors hover:bg-surface-hover"
                >
                  Показать все обновления
                  <span className="tabular font-normal text-ink-muted">
                    ещё {hiddenCount} {plural(hiddenCount, ['обновление', 'обновления', 'обновлений'])}
                  </span>
                </button>
              )}
            </Card>
          </section>

          <section className="mt-9.5">
            <div className="mb-3.5 flex flex-wrap items-baseline gap-2.5">
              <h2 className="font-display text-[19px] font-semibold tracking-[-0.01em]">В ближайших планах</h2>
              <span className="text-[13.5px] text-ink-muted">сроки примерные</span>
            </div>

            <div className="rounded-card border border-dashed border-line-dashed bg-surface-soft px-5 py-1.5">
              {visibleUpcoming.map((item) => (
                <UpcomingRow key={item.id} item={item} open={Boolean(openRows[item.id])} onToggle={() => toggleRow(item.id)} />
              ))}

              {visibleUpcoming.length === 0 && (
                <EmptyState
                  title="Сейчас в работе ничего нет"
                  description="Как только команда возьмёт задачу, она появится здесь автоматически."
                />
              )}
            </div>

            <p className="mt-3.5 text-[13px] text-ink-muted text-balance-pretty">
              Планы могут сдвигаться. Если обновление важно для вашей работы — напишите команде, мы учтём это
              в приоритетах.
            </p>
          </section>
        </>
      )}
    </>
  )
}
